import { useEffect, useState } from "react";
import api from "../api/client";

const PLANES = [
  {
    id: "gratis",
    label: "Gratis",
    precio: "$0",
    periodo: "siempre",
    colorBorde: "#d1d5db",
    colorBadge: "#f3f4f6",
    colorTexto: "#374151",
    features: ["1 catálogo digital", "Hasta 20 productos", "Pedidos por WhatsApp", "1 usuario"],
  },
  {
    id: "basico",
    label: "Básico",
    precio: "$9.99",
    periodo: "/mes",
    colorBorde: "#a5b4fc",
    colorBadge: "#eef2ff",
    colorTexto: "#4338ca",
    features: ["Productos ilimitados", "Múltiples usuarios", "Estadísticas avanzadas", "Soporte prioritario", "Sin marca de agua"],
    popular: true,
  },
  {
    id: "pro",
    label: "Pro",
    precio: "$19.99",
    periodo: "/mes",
    colorBorde: "#d8b4fe",
    colorBadge: "#faf5ff",
    colorTexto: "#7c3aed",
    features: ["Todo lo del Básico", "Múltiples catálogos", "Dominio personalizado", "API access", "Soporte 24/7"],
  },
];

function BarraUso({ label, usado, limite, color }) {
  const porcentaje = limite ? Math.min((usado / limite) * 100, 100) : 0;
  const enLimite = limite && usado >= limite;
  const cercaDelLimite = limite && porcentaje >= 80 && !enLimite;

  const colorBarra = enLimite ? "#ef4444" : cercaDelLimite ? "#f59e0b" : color;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-gray-700">{label}</span>
        <span className={`font-semibold ${enLimite ? "text-red-600" : cercaDelLimite ? "text-amber-600" : "text-gray-800"}`}>
          {usado}{limite ? ` / ${limite}` : " / ilimitados"}
        </span>
      </div>
      {limite ? (
        <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
          <div
            className="h-2.5 rounded-full transition-all duration-500"
            style={{ width: `${porcentaje}%`, background: colorBarra }}
          />
        </div>
      ) : (
        <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
          <div className="h-2.5 rounded-full bg-green-400 w-full opacity-40" />
        </div>
      )}
      {enLimite && (
        <p className="text-xs text-red-600 font-medium">
          Limite alcanzado — actualiza tu plan para agregar mas
        </p>
      )}
      {cercaDelLimite && (
        <p className="text-xs text-amber-600">
          Cerca del limite ({Math.round(porcentaje)}% usado)
        </p>
      )}
    </div>
  );
}

export default function Suscripcion() {
  const [uso, setUso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/suscripcion/")
      .then((r) => setUso(r.data))
      .catch(() => setError("No se pudo cargar la información de suscripción"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-40">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">{error}</div>
  );

  const planInfo = PLANES.find((p) => p.id === uso?.plan) || PLANES[0];
  const enLimiteProd = uso?.productos?.limite && uso.productos.usado >= uso.productos.limite;
  const enLimiteUser = uso?.usuarios?.limite && uso.usuarios.usado >= uso.usuarios.limite;
  const hayLimiteAlcanzado = enLimiteProd || enLimiteUser;

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Suscripcion</h2>
        <p className="text-gray-500 text-sm mt-1">Estado actual de tu plan y uso de recursos</p>
      </div>

      {/* Plan actual + alerta */}
      <div className={`rounded-xl border-2 p-5 ${hayLimiteAlcanzado ? "border-red-300 bg-red-50" : "border-indigo-200 bg-indigo-50"}`}>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Plan actual</p>
            <div className="flex items-center gap-2">
              <span
                className="text-xl font-bold capitalize"
                style={{ color: planInfo.colorTexto }}
              >
                {uso?.plan || "gratis"}
              </span>
              <span
                className="text-xs font-medium px-2 py-0.5 rounded-full"
                style={{ background: planInfo.colorBadge, color: planInfo.colorTexto }}
              >
                {uso?.activa ? "Activa" : "Inactiva"}
              </span>
            </div>
            {uso?.fecha_fin && (
              <p className="text-xs text-gray-500 mt-1">
                Vence el {new Date(uso.fecha_fin).toLocaleDateString("es-VE")}
              </p>
            )}
          </div>
          {hayLimiteAlcanzado && (
            <div className="bg-red-100 border border-red-300 rounded-lg px-3 py-2 text-sm text-red-700 font-medium">
              Limite alcanzado — considera actualizar tu plan
            </div>
          )}
        </div>
      </div>

      {/* Medidores de uso */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-5">
        <h3 className="font-semibold text-gray-800">Uso de recursos</h3>
        <BarraUso
          label="Productos"
          usado={uso?.productos?.usado ?? 0}
          limite={uso?.productos?.limite}
          color="#6366f1"
        />
        <BarraUso
          label="Usuarios"
          usado={uso?.usuarios?.usado ?? 0}
          limite={uso?.usuarios?.limite}
          color="#8b5cf6"
        />
      </div>

      {/* Planes */}
      <div>
        <h3 className="font-semibold text-gray-800 mb-4">Planes disponibles</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PLANES.map((plan) => {
            const esCurrent = uso?.plan === plan.id;
            return (
              <div
                key={plan.id}
                className={`bg-white rounded-xl p-6 relative ${plan.popular ? "shadow-lg" : "shadow-sm"}`}
                style={{ border: `2px solid ${esCurrent ? plan.colorBorde : plan.colorBorde}`, opacity: esCurrent ? 1 : 0.85 }}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-indigo-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Mas popular
                    </span>
                  </div>
                )}

                <div className="mb-4">
                  <span
                    className="text-xs font-medium px-2 py-1 rounded-full"
                    style={{ background: plan.colorBadge, color: plan.colorTexto }}
                  >
                    {plan.label}
                  </span>
                  <div className="mt-3">
                    <span className="text-3xl font-bold text-gray-900">{plan.precio}</span>
                    <span className="text-gray-500 text-sm">{plan.periodo}</span>
                  </div>
                </div>

                <ul className="space-y-2 mb-6">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>

                {esCurrent ? (
                  <div className="w-full text-center py-2.5 border-2 border-green-500 text-green-700 rounded-lg text-sm font-medium bg-green-50">
                    Plan actual
                  </div>
                ) : (
                  <button
                    onClick={() => alert("Integracion con pasarela de pagos proximamente")}
                    className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors ${plan.popular ? "bg-indigo-600 text-white hover:bg-indigo-700" : "border border-gray-300 text-gray-700 hover:bg-gray-50"}`}
                  >
                    {plan.id === "gratis" ? "Cambiar a Gratis" : `Contratar ${plan.label}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-center text-xs text-gray-400">
        Los pagos se procesan de forma segura. Cancela en cualquier momento.
      </p>
    </div>
  );
}
